import { AttachmentField } from '$src/components/common/AttachmentField'
import { useAttachments } from '$src/lib/hooks/useAttachments'
import { getSeasonAndYearFromDeadline } from '$src/modules/maintenance/YearCalendarCarousel/utils'
import {
  Alert,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { categoryOptions } from '@markku/utils'
import { IconCoinEuro, IconInfoCircle } from '@tabler/icons-react'
import { TaskFormData } from './types'

interface TaskFormProps {
  data: TaskFormData
  onChange: (data: TaskFormData) => void
  errors?: Record<string, string>
  disabled?: boolean
  attachments?: ReturnType<typeof useAttachments>
  targetSeason?: string
  markAsCompleted?: boolean
  isNewTask?: boolean
  hideTitle?: boolean
}

// Get Finnish name for season (genitive case)
const getSeasonName = (season: string): string => {
  const names: Record<string, string> = {
    spring: 'kevään',
    summer: 'kesän',
    fall: 'syksyn',
    winter: 'talven',
  }
  return names[season] || season
}

export function TaskForm({
  data,
  onChange,
  errors,
  disabled,
  attachments,
  targetSeason,
  markAsCompleted = false,
  isNewTask = false,
  hideTitle = false,
}: TaskFormProps) {
  const handleFieldChange = (field: keyof TaskFormData, value: any) => {
    onChange({ ...data, [field]: value })
  }

  // Check if deadline doesn't match target season
  const deadlineSeason = data.deadline
    ? getSeasonAndYearFromDeadline(data.deadline).season
    : null
  const showSeasonMismatch =
    targetSeason && deadlineSeason && deadlineSeason !== targetSeason

  // Hide deadline when creating a new task that's already completed
  // (deadline will auto-sync with completed date in parent component)
  const showDeadlineField = !markAsCompleted || !isNewTask

  return (
    <Stack gap="md">
      {!hideTitle && (
        <TextInput
          label="Tehtävän otsikko"
          placeholder="Esim. Ikkunoiden pesu"
          value={data.name}
          onChange={(e) => handleFieldChange('name', e.currentTarget.value)}
          error={errors?.name}
          required
          disabled={disabled}
        />
      )}

      {showDeadlineField && (
        <>
          <DateInput
            label="Tehtävän määräaika"
            placeholder="Valitse päivämäärä"
            value={data.deadline ? new Date(data.deadline) : new Date()}
            onChange={(value) => {
              // In Mantine v8, DateInput onChange returns a date string (e.g., '2024-01-15')
              handleFieldChange('deadline', value || '')
            }}
            error={errors?.deadline}
            required
            disabled={disabled}
          />

          {showSeasonMismatch && (
            <Alert icon={<IconInfoCircle size={20} />} color="blue">
              <Text size="sm">
                Tehtävä lisätään {getSeasonName(deadlineSeason)} listalle
                määräajan perusteella
              </Text>
            </Alert>
          )}
        </>
      )}

      <Select
        label="Kohde"
        placeholder="Valitse kohde"
        data={categoryOptions}
        value={data.category}
        onChange={(value) => handleFieldChange('category', value)}
        error={errors?.category}
        clearable
        disabled={disabled}
      />

      <Textarea
        label="Kuvaus"
        placeholder="Lisää tarkempi kuvaus tehtävästä..."
        value={data.description}
        onChange={(e) =>
          handleFieldChange('description', e.currentTarget.value)
        }
        error={errors?.description}
        minRows={3}
        disabled={disabled}
      />

      {(!markAsCompleted || !isNewTask) && (
        <NumberInput
          label="Arvioitu kustannus (vapaaehtoinen)"
          placeholder="0"
          leftSection={<IconCoinEuro size={16} />}
          rightSection={
            <Text size="sm" c="dimmed">
              €
            </Text>
          }
          value={data.estimatedCost || ''}
          onChange={(value) => handleFieldChange('estimatedCost', value)}
          min={0}
          max={100000}
          decimalScale={2}
          disabled={disabled}
          description="Auttaa budjetoinnissa ja vuosisuunnittelussa"
        />
      )}

      {attachments && (
        <div>
          <Stack gap="xs">
            <TextInput
              label="Liitteet"
              placeholder="Liitteet tallennetaan tehtävän tallennuksen yhteydessä"
              disabled
              style={{ display: 'none' }}
            />
            <AttachmentField
              attachments={attachments.attachments}
              isUploading={attachments.isUploading}
              uploadFile={attachments.uploadFile}
              removeAttachment={attachments.removeAttachment}
              disabled={disabled}
            />
          </Stack>
        </div>
      )}
    </Stack>
  )
}
